import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../apiClient';
import { Profile } from '../../hooks/useAuth';
import { Save, Eye, EyeOff, AlertCircle, CheckCircle2, Lock } from 'lucide-react';
import { Card } from '../ui/Card';

const SETTING_META: Record<string, { description: string; is_sensitive: boolean }> = {
  TOYYIBPAY_URL:           { description: 'API endpoint for ToyyibPay gateway', is_sensitive: false },
  TOYYIBPAY_SECRET_KEY:    { description: 'Private secret key for API authentication', is_sensitive: true },
  TOYYIBPAY_CATEGORY_CODE: { description: 'Merchant category code from ToyyibPay dashboard', is_sensitive: false },
};

interface Setting { key: string; value: string; }

export const SystemSettings: React.FC<{ profile: Profile }> = () => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSensitive, setShowSensitive] = useState<Record<string, boolean>>({});

  useEffect(() => {
    apiFetch('/admin/settings')
      .then((data) => {
        if (data.success) setSettings(data.settings);
        else setError(data.message);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleUpdate = async (key: string, value: string) => {
    setSaving(key);
    setError(null);
    setSuccess(null);
    try {
      const data = await apiFetch(`/admin/settings/${key}`, {
        method: 'PATCH',
        body: JSON.stringify({ value }),
      });
      if (!data.success) throw new Error(data.message);
      setSuccess(`"${key}" updated successfully.`);
      setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value } : s)));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading) return (
    <div className="p-12 flex items-center justify-center">
      <p className="label-caps text-ink-faint">Loading settings...</p>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-10 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-3">
        <div>
          <h2 className="display text-2xl md:text-3xl text-ink">System configuration</h2>
          <p className="label-caps text-ink-faint mt-2">Payment gateway environment</p>
        </div>
        <div className="bg-inverse text-ink-inverse px-4 py-3 rounded-sm flex items-center gap-3">
          <Lock size={16} className="text-accent" />
          <span className="label-caps">Super admin session</span>
        </div>
      </div>

      {error && (
        <div className="p-4 flex items-center gap-3 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded-sm border border-red-200 dark:border-red-900 text-xs font-semibold">
          <AlertCircle size={16} className="shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="p-4 flex items-center gap-3 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 rounded-sm border border-green-200 dark:border-green-900 text-xs font-semibold">
          <CheckCircle2 size={16} className="shrink-0" /> {success}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:gap-6">
        {settings.map((setting) => {
          const meta = SETTING_META[setting.key] || { description: setting.key, is_sensitive: false };
          return (
            <Card key={setting.key} accent={meta.is_sensitive} className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="display text-base text-ink">{setting.key}</h3>
                    {meta.is_sensitive && <Lock size={14} className="text-accent" />}
                  </div>
                  <p className="label-caps text-ink-faint">{meta.description}</p>
                </div>
                <div className="flex-1 max-w-2xl">
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <input
                        type={meta.is_sensitive && !showSensitive[setting.key] ? 'password' : 'text'}
                        value={setting.value}
                        onChange={(e) => setSettings((prev) => prev.map((s) => s.key === setting.key ? { ...s, value: e.target.value } : s))}
                        className="w-full min-h-[44px] px-4 bg-surface border border-line-strong rounded-sm font-mono text-sm text-ink outline-none focus:border-accent transition-colors"
                      />
                      {meta.is_sensitive && (
                        <button
                          onClick={() => setShowSensitive((p) => ({ ...p, [setting.key]: !p[setting.key] }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink"
                          aria-label={showSensitive[setting.key] ? 'Hide value' : 'Show value'}
                        >
                          {showSensitive[setting.key] ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => handleUpdate(setting.key, setting.value)}
                      disabled={saving === setting.key}
                      className="min-h-[44px] px-4 bg-inverse text-ink-inverse dark:bg-accent dark:text-accent-contrast rounded-sm hover:opacity-90 transition-opacity disabled:opacity-40"
                      aria-label={`Save ${setting.key}`}
                    >
                      {saving === setting.key
                        ? <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        : <Save size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
